import {Component, inject, Input, ChangeDetectionStrategy} from '@angular/core';
import {FaIconComponent} from '@fortawesome/angular-fontawesome';
import {faCircleCheck, faCircleXmark, faClipboardList} from '@fortawesome/pro-solid-svg-icons';
import {faSpinnerThird} from '@fortawesome/pro-duotone-svg-icons';
import {TooltipDirective} from '@buildinginformationcloud/bic-ui-components';
import {SimpleCheck} from '../../../api';
import {CheckReportService} from '../../../services/checks/check-report.service';
import {SkeletonLoadingDirective} from '../../../directives/skeleton-loading.directive';

/**
 * Kennzahlen der ausgeführten Prüfläufe: Anzahl, abgeschlossen, laufend, fehlgeschlagen –
 * jeweils für die letzten 30 Tage (laufende unabhängig vom Datum).
 *
 * Jede Box trägt Icon und Überschrift links, den Wert rechts – wie die Status-Boxen der
 * Tasks-Kachel. Weil der Inhalt immer in einer Zeile liegt, braucht die Kachel keine
 * Höhenmessung mehr.
 *
 * In der eigenen Kachel (`fill`) steht darunter eine Rangliste der häufigsten Prüftypen; sie
 * nutzt die Höhe, die die Boxen übrig lassen.
 */
@Component({
  selector: 'app-dashboard-check-metrics',
  templateUrl: './dashboard-check-metrics.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [FaIconComponent, TooltipDirective, SkeletonLoadingDirective]
})
export class DashboardCheckMetricsComponent {

  @Input() checks: SimpleCheck[] = [];
  @Input() loading = false;
  /**
   * Anordnung der vier Boxen:
   * `fill`: untereinander, gleichmäßig über die Kachelhöhe verteilt (eigene Kachel).
   * `compact`: 2x2 nach Inhaltshöhe – über den Prüfläufen, wo sie wenig Höhe kosten sollen.
   */
  @Input() variant: 'fill' | 'compact' = 'compact';

  private checkReportService = inject(CheckReportService);

  private readonly WINDOW_DAYS = 30;
  /** So viele Prüftypen zeigt die Rangliste – darunter wird die Kachel zur Tabelle. */
  private readonly MAX_TYPES = 5;

  protected readonly faCircleCheck = faCircleCheck;
  protected readonly faCircleXmark = faCircleXmark;
  protected readonly faClipboardList = faClipboardList;
  protected readonly faSpinnerThird = faSpinnerThird;

  get fillsHeight(): boolean {
    return this.variant === 'fill';
  }

  get total(): number {
    return this.recentRuns.length;
  }

  get completed(): number {
    return this.checkReportService.countByStatus(this.recentRuns, 'COMPLETED');
  }

  /** Laufende Prüfungen zählen unabhängig vom Zeitfenster – sie sind jetzt relevant. */
  get running(): number {
    return this.checkReportService.countByStatus(this.checkReportService.activeRuns(this.checks), 'RUNNING');
  }

  get failed(): number {
    return this.checkReportService.countByStatus(this.recentRuns, 'FAILED');
  }

  /**
   * Häufigste Prüftypen im Zeitfenster, absteigend. `percent` ist der Anteil am häufigsten Typ –
   * der Balken vergleicht also untereinander, nicht mit der Gesamtzahl.
   */
  get topCheckTypes(): { label: string, count: number, percent: number }[] {
    const counts = new Map<string, number>();
    for (const run of this.recentRuns) {
      const label = this.checkReportService.getCheckTypeLabel(run);
      counts.set(label, (counts.get(label) ?? 0) + 1);
    }

    const ranked = [...counts.entries()]
      .sort(([labelA, countA], [labelB, countB]) => countB - countA || labelA.localeCompare(labelB))
      .slice(0, this.MAX_TYPES);
    const highest = ranked[0]?.[1] ?? 0;

    return ranked.map(([label, count]) => ({
      label,
      count,
      percent: highest === 0 ? 0 : Math.round(count / highest * 100)
    }));
  }

  runLabel(count: number): string {
    return count === 1 ? '1 Ausführung' : `${count} Ausführungen`;
  }

  private get recentRuns(): SimpleCheck[] {
    return this.checkReportService.runsWithinDays(this.checks, this.WINDOW_DAYS);
  }
}
